import { Component, Input, OnInit, ViewChild, } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Address } from '@models/address/address';
import { EntityData } from '@models/companies/entity-data';
import { IndividualData } from '@models/individuals/individual-data';
import { ShareholderRelationshipDetails } from '@models/relationships/relationship';
import { SecurityHolding } from '@models/shares/security-holding';
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from '@models/shares/security-registry-record';
import { DividerComponent } from '@shared/components/commons/divider/divider.component';
import { InputNumberComponent } from '@shared/controls/input-number/input-number.component';
import { Guid } from '@shared/utils/guid.helper';
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import {
  ShareSecurityHoldingsDropdownComponent
} from '../../../controls/share-security-holdings-dropdown/share-security-holdings-dropdown.component';
import { CompanySecurityTransfer } from "../../../models/shares";
import { totalNumberOfSharesCustomErrors } from '../../../models/shares/company-security-cancellation.model';
import { ShareholderTypesEnum } from '../../../models/shares/company-security-issue.model';
import {
  IndividualDataFormGroupComponent,
  IndividualDataFormGroupControls
} from '../../../reusable-form-groups/individual-data-form-group/individual-data-form-group.component';
import {
  ReusableFormGroupComponent
} from '../../../reusable-form-groups/reusable-form-group/reusable-form-group.component';
import {
  SelectShareholderFormComponent,
  SelectShareholderFormControls
} from '../../../reusable-form-parts/select-shareholder-form/select-shareholder-form.component';

export interface IShareTransferToFormGroupControls {
  securityHoldingId: FormControl<string | null>,
  numberOfSharesTransferred: FormControl<number | null>,
  selectShareholder: FormGroup<SelectShareholderFormControls>
}

export type IShareTransferToFormGroup = FormGroup<IShareTransferToFormGroupControls>;

@Component({
  selector: 'app-share-transfer-to-block',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DividerComponent,
    SelectShareholderFormComponent,
    InputNumberComponent,
    ShareSecurityHoldingsDropdownComponent
  ],
  templateUrl: 'share-transfer-to-block.component.html',
})
export class CompanySecurityTransferToBlock extends ReusableFormGroupComponent<IShareTransferToFormGroup> implements OnInit {
  @ViewChild(SelectShareholderFormComponent) securityHoldingsDropdownTo!: SelectShareholderFormComponent;

  @Input({ required: false }) authorisedSecurityRegistryRecords: SecurityRegistryRecord[] = [];

  readonly totalNumberOfSharesCustomErrors = totalNumberOfSharesCustomErrors;

  // values set by transfer from block
  transferSecurityRegistryRecords: SecurityRegistryRecord[] = [];
  selectedShareholderShareType: SecurityHolding | null = null;
  securityRegistryRecordsHoldings: SecurityHolding[] = [];

  selectedIndividualShareholder: IndividualHolderModel | null = null;
  selectedCompanyShareholder: EntityData | null = null;

  public static override defineFormControls(): IShareTransferToFormGroupControls {
    return {
      securityHoldingId: new FormControl<string | null>(null),
      numberOfSharesTransferred: new FormControl<number | null>(null),
      selectShareholder: SelectShareholderFormComponent.defineForm(),
    };
  }

  public setupChange(change: CompanySecurityTransfer) {
    this.form.patchValue(change.transferTo);
    this.disableReadonlyControls();

    this.form.controls.securityHoldingId.setValue(change.transferFrom.securityHoldingId ?? null);

    this.securityHoldingsDropdownTo.setupChange(change);
  }

  public disableReadonlyControls(): void {
    setControlDisabled(this.form.controls.numberOfSharesTransferred);
    setControlDisabled(this.form.controls.securityHoldingId);
  }

  private get selectedSecurityHoldersTo(): (IndividualHolderModel | CorporateHolderModel)[] | null {
    const securityRegistryRecordGroup = this.form.controls.selectShareholder.controls.securityRegistryRecordGroup.value;
    return securityRegistryRecordGroup ? securityRegistryRecordGroup[0].holders : null;
  }

  getSecurityHoldersTo(): (IndividualHolderModel | CorporateHolderModel)[] {
    switch (this.form.controls.selectShareholder.value.shareholderType) {
      case ShareholderTypesEnum.ExistingShareholder: {
        const selectedHolders = this.selectedSecurityHoldersTo!;
        const holders = this.buildShortBaseHolders(selectedHolders.filter(Boolean));
        return holders
          .map((shareholder) => {
            shareholder.details.isBeneficialOwner = !this.form.controls.selectShareholder.controls.isNotBeneficialOwner.value!;
            shareholder.details.beneficialOwner = this.form.controls.selectShareholder.value.beneficialOwner!;

            return shareholder;
          });
      }
      case ShareholderTypesEnum.Individual: {
        const individualData = IndividualDataFormGroupComponent.toIndividualData(this.form.controls.selectShareholder.controls.newIndividualShareholder as FormGroup<Partial<IndividualDataFormGroupControls>>);
        const newIndividualSecurityHolder = new IndividualHolderModel({
          individualData,
        });
        return [this.buildShortIndividualHolderModel(newIndividualSecurityHolder)];
      }
      case ShareholderTypesEnum.Corporate: {
        const entityData = new EntityData({
          name: this.form.controls.selectShareholder.value.name ?? '',
          entityNumber: this.form.controls.selectShareholder.value.acn ?? '',
          registeredAddress: new Address(this.form.controls.selectShareholder.value.registeredAddress as Partial<Address>)
        });
        const newCorporateSecurityHolder = new CorporateHolderModel({ entityData });
        newCorporateSecurityHolder.details.authorisedSignatories = this.getHolderAuthorisedSignatories(newCorporateSecurityHolder);
        return [this.buildShortCorporateHolderModel(newCorporateSecurityHolder)];
      }
      case ShareholderTypesEnum.JointShareholder: {
        return this.buildShortBaseHolders(this.form.controls.selectShareholder.value.joint ?? []);
      }
      default:
        return [];
    }
  }

  private buildShortBaseHolders(holders: (IndividualHolderModel | CorporateHolderModel)[]): (IndividualHolderModel | CorporateHolderModel)[] {
    return holders.map(holder => {
      if (holder.$type === IndividualHolderModel.$type) {
        return this.buildShortIndividualHolderModel(holder as IndividualHolderModel);
      } else if (holder.$type === CorporateHolderModel.$type) {
        return this.buildShortCorporateHolderModel(holder as CorporateHolderModel);
      }
      return holder;
    });
  }

  private buildShortIndividualHolderModel(individualHolder: IndividualHolderModel): IndividualHolderModel {
    const isBeneficialOwner = this.form.controls.selectShareholder.value.isNotBeneficialOwner !== null
      ? !this.form.controls.selectShareholder.value.isNotBeneficialOwner
      : individualHolder.details.isBeneficialOwner;
    const individualHolderModel = {
      $type: IndividualHolderModel.$type,
      relationshipId: individualHolder.relationshipId || Guid.EmptyGuid,
      details: {
        isBeneficialOwner,
        $type: new ShareholderRelationshipDetails().$type,
        beneficialOwner: this.form.controls.selectShareholder.value.beneficialOwner ?? individualHolder.details.beneficialOwner ?? '',
      } as unknown as ShareholderRelationshipDetails,
      individualData: {
        firstName: individualHolder.individualData.firstName,
        middleName1: individualHolder.individualData.middleName1,
        middleName2: individualHolder.individualData.middleName2,
        lastName: individualHolder.individualData.lastName,
        address: individualHolder.individualData.address
      } as IndividualData,
    } as IndividualHolderModel;

    if (individualHolder.individualId) {
      individualHolderModel.individualId = individualHolder.individualId;
    }

    return individualHolderModel;
  }

  private buildShortCorporateHolderModel(corporateHolder: CorporateHolderModel): CorporateHolderModel {
    const isBeneficialOwner = this.form.controls.selectShareholder.value.isNotBeneficialOwner !== null
      ? !this.form.controls.selectShareholder.value.isNotBeneficialOwner
      : corporateHolder.details.isBeneficialOwner;
    return {
      $type: 'CorporateHolderModel',
      details: {
        isBeneficialOwner,
        $type: new ShareholderRelationshipDetails().$type,
        authorisedSignatories: this.getHolderAuthorisedSignatories(corporateHolder),
        beneficialOwner: this.form.controls.selectShareholder.value.beneficialOwner ?? corporateHolder.details.beneficialOwner ?? '',
      } as unknown as ShareholderRelationshipDetails,
      entityData: {
        name: corporateHolder.entityData.name,
        entityNumber: corporateHolder.entityData.entityNumber,
        registeredAddress: corporateHolder.entityData.registeredAddress
      } as EntityData,
      relationshipId: corporateHolder.relationshipId || Guid.EmptyGuid,
    } as CorporateHolderModel;
  }

  private getHolderAuthorisedSignatories(holder: CorporateHolderModel): IndividualData[] {
    return this.authorisedSecurityRegistryRecords
      .find(record => record.holders.some(h => h.name === holder.name))
      ?.holders[0].details.authorisedSignatories ?? [];
  }
}
